// EXPORTAR EL LLENÇ A PDF.
//
// Sense cap dependència nova. L'aplicació no en portava cap de PDF i afegir-n'hi una hauria estat
// mig mega de codi per a un botó — i, a més, totes les que rasteritzen el dibuix el converteixen
// en una imatge. Aquí el llenç ja és SVG, o sigui que **passant-lo per la impressió del navegador
// el PDF surt vectorial**: es pot ampliar sense que es piqui i el text es pot seleccionar.
//
// El camí és un `iframe` amagat i no una finestra nova, que els blocadors de finestres emergents
// la tallarien.
//
// ── QUE SURTI SENCER I CENTRAT ──────────────────────────────────────────────────────────────
//
// El llenç de la pantalla ensenya el tros que hi cap, amb el zoom i el desplaçament que tinguis.
// El PDF ha d'ensenyar-ho **tot**, i per això no es copia la vista: es calcula la capsa que conté
// el dibuix sencer —`getBBox()` del grup de contingut, que dona les mides reals encara que estigui
// fora de la pantalla— i es posa com a `viewBox`. Amb `preserveAspectRatio="xMidYMid meet"` el
// dibuix queda centrat al full i escalat fins on hi càpiga, sense deformar-se.

const MARGE = 24;   // marge al voltant del dibuix, en unitats del llenç

// EL GRUP QUE PORTA EL DIBUIX, i no el `<svg>` sencer.
//
// Els dos llenços marquen amb `data-contingut` el grup on hi ha el model —el mateix que porta el
// zoom i el desplaçament—. Tota la resta que penja del `<svg>` és decorat de pantalla, i n'hi ha
// prou amb una peça per fer malbé l'enquadrament:
//
//     <rect width="100%" height="100%" fill="url(#reixa)" />     ← el fons de quadrícula
//
// Aquell rectangle **no és transparent i no és a `defs`**, o sigui que comptava. I a més el seu
// `getBBox()` va en coordenades de la finestra mentre que el dels nodes va en coordenades del
// grup transformat: la unió eren dos sistemes de coordenades barrejats més una capsa de la mida
// del full a l'origen. El PDF sortia amb el diagrama petit i descentrat i la quadrícula estampada
// a tot el paper — justament les dues coses que aquest fitxer promet que no passen.
export const arrelDeContingut = (svg) => svg.querySelector('[data-contingut]') || svg;

export function capsaDelContingut(svg) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const node of arrelDeContingut(svg).querySelectorAll('circle, text, path, line, rect, polygon')) {
    // Les definicions (puntes de fletxa) i les taques invisibles no compten.
    if (node.closest('defs')) continue;
    if (node.getAttribute('fill') === 'transparent') continue;
    if (node.getAttribute('stroke') === 'transparent') continue;
    let b;
    try { b = node.getBBox(); } catch { continue; }
    if (!b || (!b.width && !b.height)) continue;
    x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width); y1 = Math.max(y1, b.y + b.height);
  }
  if (!Number.isFinite(x0)) return null;
  return { x: x0 - MARGE, y: y0 - MARGE, w: x1 - x0 + MARGE * 2, h: y1 - y0 + MARGE * 2 };
}

export function exportaLlencPDF(svgOriginal, titol, textPeu) {
  if (!svgOriginal) return false;
  const capsa = capsaDelContingut(svgOriginal);
  if (!capsa) return false;

  const copia = svgOriginal.cloneNode(true);
  // Fora tot el que no és el dibuix: el fons de quadrícula i qualsevol altre decorat de pantalla.
  // `defs` es queda, que hi viuen les puntes de fletxa i les màscares que el dibuix referencia.
  if (copia.querySelector('[data-contingut]')) {
    for (const fill of [...copia.children]) {
      if (fill.tagName.toLowerCase() !== 'defs' && !fill.hasAttribute('data-contingut')) fill.remove();
    }
  }
  // El zoom i el desplaçament de la pantalla no van al paper: el `viewBox` ja enquadra el
  // dibuix sencer, i deixar-hi la transformació el mouria fora del full.
  copia.querySelectorAll('g[transform]').forEach((g) => {
    if (/scale|translate/.test(g.getAttribute('transform') || '')) g.removeAttribute('transform');
  });
  // Fora tot el que és per clicar i no es veu.
  copia.querySelectorAll('[fill="transparent"], [stroke="transparent"]').forEach((n) => n.remove());
  copia.setAttribute('viewBox', `${capsa.x} ${capsa.y} ${capsa.w} ${capsa.h}`);
  copia.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  copia.setAttribute('width', '100%');
  copia.setAttribute('height', '100%');
  copia.removeAttribute('class');
  copia.removeAttribute('style');

  // Full apaïsat si el dibuix és més ample que alt, que és el cas corrent d'un diagrama causal.
  const apaisat = capsa.w >= capsa.h;

  const marc = document.createElement('iframe');
  marc.setAttribute('aria-hidden', 'true');
  marc.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(marc);

  const doc = marc.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapa(titol)}</title>
<style>
  @page { size: A4 ${apaisat ? 'landscape' : 'portrait'}; margin: 14mm; }
  html, body { margin: 0; padding: 0; height: 100%; }
  body { display: flex; flex-direction: column;
         font: 11pt/1.4 ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
         color: #1B1A17; }
  h1 { margin: 0 0 2mm; font-size: 13pt; font-weight: 650; }
  .peu { margin: 0 0 4mm; font-size: 9pt; color: #6B675E; }
  /* El dibuix s'endú tot l'espai que queda i s'hi centra. */
  .dibuix { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; }
  .dibuix svg { max-width: 100%; max-height: 100%; }
</style></head><body>
<h1>${escapa(titol)}</h1>
<p class="peu">${escapa(textPeu)}</p>
<div class="dibuix">${copia.outerHTML}</div>
</body></html>`);
  doc.close();

  const imprimeix = () => {
    try {
      marc.contentWindow.focus();
      marc.contentWindow.print();
    } finally {
      // Es treu després, que si no el diàleg d'impressió es queda sense document a sota.
      setTimeout(() => marc.remove(), 1000);
    }
  };
  // `onload` de l'iframe no sempre arriba amb `document.write`; es fa servir el que arribi abans.
  if (doc.readyState === 'complete') setTimeout(imprimeix, 50);
  else marc.onload = () => setTimeout(imprimeix, 50);
  return true;
}

function escapa(t) {
  return String(t || '').replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

export default exportaLlencPDF;
